import { useCallback, useEffect, useState } from "react";
import { ArrowSquareOut, DownloadSimple, ArrowClockwise } from "@phosphor-icons/react";
import PolicyLayout from "./PolicyLayout";
import { fetchPublicPolicy, policyPdfUrl, formatPublishedDate } from "../../lib/publicLegal";

// M2 — DB-backed current-policy viewer for the four fixed public policy pages. Loads current
// metadata, embeds the current PDF (unauthenticated direct URL), and always offers Open + Download.
// NO_CURRENT_POLICY / unknown renders a generic 404 IN PLACE (URL preserved; no redirect).
export default function PolicyDocumentView({ slug, fallbackTitle }) {
  const [status, setStatus] = useState("loading"); // loading | ready | notfound | error
  const [meta, setMeta] = useState(null);

  const load = useCallback(() => {
    setStatus("loading");
    setMeta(null);
    fetchPublicPolicy(slug)
      .then((m) => { setMeta(m); setStatus("ready"); })
      .catch((err) => { setStatus(err && err.notFound ? "notfound" : "error"); });
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // No current published policy (or unknown slug) → generic not-found rendered IN PLACE. The browser
  // stays on the current policy URL; no navigation, no history change, no app-wide route change.
  if (status === "notfound") {
    return (
      <PolicyLayout title="Page not found" testid={`policy-${slug}-page`}>
        <p className="text-[15px] text-[hsl(var(--foreground))]" data-testid="policy-not-found">
          The page you’re looking for could not be found.
        </p>
      </PolicyLayout>
    );
  }

  const title = (meta && meta.title) || fallbackTitle;

  if (status === "loading") {
    return (
      <PolicyLayout title={title} testid={`policy-${slug}-page`}>
        <div className="text-sm text-[hsl(var(--muted))]" data-testid="policy-loading">
          Loading policy…
        </div>
      </PolicyLayout>
    );
  }

  if (status === "error") {
    return (
      <PolicyLayout title={title} testid={`policy-${slug}-page`}>
        <div className="space-y-4" data-testid="policy-error">
          <p className="text-[15px] text-[hsl(var(--foreground))]">
            We couldn’t load this policy right now. Please try again.
          </p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 btn-secondary text-sm"
            data-testid="policy-retry"
          >
            <ArrowClockwise size={15} weight="bold" /> Retry
          </button>
        </div>
      </PolicyLayout>
    );
  }

  const inlineUrl = policyPdfUrl(slug);
  const downloadUrl = policyPdfUrl(slug, { download: true });

  return (
    <PolicyLayout title={title} testid={`policy-${slug}-page`}>
      <div className="space-y-6">
        <p className="overline" data-testid="policy-version-line">
          Version {meta.system_version} · Published {formatPublishedDate(meta.published_at)}
        </p>

        <div className="flex flex-wrap gap-3" data-testid="policy-actions">
          <a
            href={inlineUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 btn-secondary text-sm"
            data-testid="policy-open"
          >
            <ArrowSquareOut size={15} weight="bold" /> Open PDF
          </a>
          <a
            href={downloadUrl}
            className="inline-flex items-center gap-1.5 btn-secondary text-sm"
            data-testid="policy-download"
          >
            <DownloadSimple size={15} weight="bold" /> Download PDF
          </a>
        </div>

        <div
          className="w-full rounded-[3px] border border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--muted)/0.06)]"
          data-testid="policy-viewer"
        >
          <iframe
            src={inlineUrl}
            title={`${title} — current version PDF`}
            className="w-full h-[70vh] min-h-[480px]"
            data-testid="policy-iframe"
          />
        </div>
      </div>
    </PolicyLayout>
  );
}
